import { type ReactNode } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-600 to-teal-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -left-10 bottom-0 w-64 h-64 bg-teal-500/30 rounded-full pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 px-4 py-1.5 rounded-full text-sm mb-5">
            <i className="ri-shield-check-line"></i>
            <span>藍舍AI地產 · 隱私保護</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">隱私權政策</h1>
          <p className="text-white/80 text-base max-w-xl mx-auto">
            我們致力於保護您的個人資料安全，請詳閱以下說明。
          </p>
          <p className="text-white/60 text-sm mt-4">最後更新日期：2026年03月18日</p>
        </div>
      </section>

      {/* Beta 測試版聲明 */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-start gap-3">
          <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>測試版公告：</strong>本平台目前處於測試（Beta）階段，隱私政策內容為試營運性質，可能視法規或服務調整而更新。如有隱私相關疑慮，歡迎透過電子郵件聯繫我們。
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-14">

        {/* 目錄 */}
        <nav className="bg-white rounded-xl p-6 mb-10 border border-gray-100" aria-label="目錄">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">目錄</h2>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              ['1', '資訊收集範圍'],
              ['2', '資訊使用目的'],
              ['3', '資訊分享原則'],
              ['4', 'Cookie 使用說明'],
              ['5', '資料安全保護'],
              ['6', '您的隱私權利'],
              ['7', '未成年人保護'],
              ['8', '政策變更通知'],
            ].map(([num, label]) => (
              <li key={num}>
                <a
                  href={`#section-${num}`}
                  className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                >
                  <span className="w-6 h-6 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{num}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-10">

          <Section id="section-1" icon="ri-database-2-line" title="1. 資訊收集範圍">
            <p>當您使用「藍舍AI地產」平台時，我們可能收集以下類型的個人資訊：</p>
            <ul className="list-disc pl-5 space-y-1 mt-3">
              <li><strong>帳號資訊：</strong>姓名、電子郵件地址、手機號碼、密碼（加密儲存）</li>
              <li><strong>房源資訊：</strong>您發布或瀏覽的房源地址、照片、描述及租金資訊</li>
              <li><strong>通訊記錄：</strong>您與房東或租客之間的站內訊息內容</li>
              <li><strong>使用行為：</strong>瀏覽紀錄、搜尋關鍵字、點擊行為及偏好設定</li>
              <li><strong>裝置資訊：</strong>IP 位址、瀏覽器類型、作業系統及裝置識別碼</li>
              <li><strong>位置資訊：</strong>您允許後，用於推薦附近房源的概略地理位置</li>
            </ul>
          </Section>

          <Section id="section-2" icon="ri-focus-3-line" title="2. 資訊使用目的">
            <p>我們收集的資訊將用於以下目的：</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {[
                { icon: 'ri-user-settings-line', text: '提供帳號管理與身份驗證服務' },
                { icon: 'ri-home-line', text: '媒合房東與租客的租屋需求' },
                { icon: 'ri-sparkling-2-line', text: '提供 AI 智能房源推薦與分析' },
                { icon: 'ri-mail-send-line', text: '發送系統通知、訊息提醒與行銷資訊' },
                { icon: 'ri-shield-check-line', text: '偵測詐騙行為與維護平台安全' },
                { icon: 'ri-bar-chart-line', text: '改善平台功能與用戶體驗分析' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-teal-50 rounded-lg p-3">
                  <i className={`${item.icon} text-teal-500 mt-0.5 flex-shrink-0`}></i>
                  <span className="text-sm text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section id="section-3" icon="ri-share-line" title="3. 資訊分享原則">
            <p>我們<strong>不會</strong>出售您的個人資訊。僅在以下情況下可能分享：</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>服務提供商：</strong>協助我們提供服務的受信任第三方（如雲端儲存、郵件服務），這些提供商受保密義務約束</li>
              <li><strong>法律要求：</strong>在符合法律法規要求或政府機關調查時，依法提供必要資訊</li>
              <li><strong>用戶同意：</strong>在您明確同意的前提下，與特定第三方分享相關資訊</li>
              <li><strong>業務移轉：</strong>若發生合併、收購等情況，資料可能依相關規定移轉</li>
            </ul>
          </Section>

          <Section id="section-4" icon="ri-cookie-line" title="4. Cookie 使用說明">
            <p>我們使用 Cookie 及類似技術來提升您的使用體驗：</p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-teal-50">
                    <th className="text-left p-3 font-semibold text-gray-700 rounded-tl-lg border border-gray-100">類型</th>
                    <th className="text-left p-3 font-semibold text-gray-700 border border-gray-100">用途</th>
                    <th className="text-left p-3 font-semibold text-gray-700 rounded-tr-lg border border-gray-100">是否必要</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['必要性 Cookie', '維持登入狀態、保存偏好設定', '是'],
                    ['功能性 Cookie', '記住您的搜尋條件與房源偏好', '否'],
                    ['分析性 Cookie', '了解用戶行為以改善平台', '否'],
                    ['行銷性 Cookie', '提供個人化廣告與房源推薦', '否'],
                  ].map(([type, purpose, required], i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-3 font-medium text-gray-800 border border-gray-100">{type}</td>
                      <td className="p-3 text-gray-600 border border-gray-100">{purpose}</td>
                      <td className="p-3 border border-gray-100">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${required === '是' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                          {required}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-3">您可透過瀏覽器設定管理 Cookie，但停用部分 Cookie 可能影響平台功能。</p>
          </Section>

          <Section id="section-5" icon="ri-lock-2-line" title="5. 資料安全保護">
            <p>我們採取多重安全措施保護您的個人資料：</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {[
                { icon: 'ri-shield-flash-line', title: 'SSL 加密傳輸', desc: '所有資料傳輸均使用 TLS/SSL 加密' },
                { icon: 'ri-key-2-line', title: '密碼加密儲存', desc: '使用 bcrypt 演算法加密儲存密碼' },
                { icon: 'ri-server-line', title: '安全伺服器', desc: '資料存放於通過安全認證的雲端平台' },
              ].map((item, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-3">
                    <i className={`${item.icon} text-2xl text-teal-500`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="section-6" icon="ri-user-heart-line" title="6. 您的隱私權利">
            <p>依據相關個人資料保護法規，您享有以下權利：</p>
            <ul className="space-y-3 mt-4">
              {[
                { right: '查詢與閱覽權', desc: '您有權查詢我們持有的您的個人資料' },
                { right: '更正與補充權', desc: '您有權要求更正不正確或不完整的資料' },
                { right: '刪除權', desc: '在特定情況下，您可要求刪除您的個人資料' },
                { right: '可攜帶權', desc: '您有權以常見格式取得您的資料副本' },
                { right: '撤回同意權', desc: '您可隨時撤回對特定資料處理活動的同意' },
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0"></span>
                  <div>
                    <strong className="text-gray-800">{item.right}：</strong>
                    <span className="text-gray-600 text-sm">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="bg-teal-50 rounded-xl p-4 mt-5 flex items-start gap-3">
              <i className="ri-mail-line text-teal-500 mt-0.5 flex-shrink-0"></i>
              <p className="text-sm text-gray-700">如需行使上述權利，請聯絡我們：<a href="mailto:info@ailanshe.com" className="text-teal-600 font-medium hover:underline">info@ailanshe.com</a>，我們將於 30 個工作日內回覆。</p>
            </div>
          </Section>

          <Section id="section-7" icon="ri-parent-line" title="7. 未成年人保護">
            <p>本平台服務對象為 <strong>18 歲以上成年人</strong>。我們不會故意收集未成年人的個人資料。</p>
            <p className="mt-3">若您認為未成年人已向我們提供個人資料，請立即透過以下方式聯繫我們，我們將盡快刪除相關資料：</p>
            <a href="mailto:info@ailanshe.com" className="inline-flex items-center gap-2 mt-3 text-teal-600 font-medium hover:underline">
              <i className="ri-mail-line"></i>info@ailanshe.com
            </a>
          </Section>

          <Section id="section-8" icon="ri-notification-3-line" title="8. 政策變更通知">
            <p>我們可能不定期更新本隱私政策。發生重大變更時，我們將透過以下方式通知您：</p>
            <ul className="list-disc pl-5 space-y-1.5 mt-3 text-sm text-gray-700">
              <li>在平台首頁顯示顯著公告</li>
              <li>向您的註冊電子郵件發送通知信</li>
              <li>更新本頁面頂端的「最後更新日期」</li>
            </ul>
            <p className="mt-4 text-gray-600">繼續使用本平台服務，即表示您接受更新後的隱私政策。</p>
          </Section>

        </div>

        {/* Contact Block */}
        <div className="mt-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 text-white text-center">
          <i className="ri-customer-service-2-line text-4xl mb-3 block"></i>
          <h3 className="text-xl font-bold mb-2">有任何疑問？</h3>
          <p className="text-white/80 text-sm mb-5">我們的隱私保護團隊隨時為您服務</p>
          <a
            href="mailto:info@ailanshe.com"
            className="inline-flex items-center gap-2 bg-white text-teal-600 px-6 py-2.5 rounded-full font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            聯絡我們
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ id, icon, title, children }: { id: string; icon: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="bg-white rounded-xl p-7 border border-gray-100">
      <h2 className="flex items-center gap-2.5 text-lg font-bold text-gray-900 mb-5 pb-4 border-b border-gray-100">
        <span className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
          <i className={`${icon} text-teal-500 text-lg`}></i>
        </span>
        {title}
      </h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
